import { animationTransition } from '@shared/utils/animationTransition';
import { Box, css, styled } from '@withjoy/joykit';

/**
 * Hide children during loading while keeping the container visible with its background.
 * This prevents a white flash during component transitions (e.g., skeleton → content)
 * by ensuring the themed background remains visible even when content is loading.
 */
const hideOnLoading = () => css`
  > * {
    visibility: hidden;
    opacity: 0;
  }
`;

const showOnLoading = () => css`
  visibility: initial;
  transition: ${animationTransition('opacity', 'background', 'background-color')};
`;

export const StyledBoxBrannan = styled(Box)<{
  loading: boolean;
  isPreviewing: boolean;
  customBackgroundColor?: string;
  loadingBackgroundColor?: string;
  customTextColor?: string;
}>`
  pointer-events: ${({ isPreviewing }) => (isPreviewing ? 'none' : 'all')};
  /* This overflow is necessary to prevent the outer frame from scrolling horizontally when the artwork is too wide */
  overflow-x: clip;
  /* Art sprites sit above page-width photos but below full-width (100vw) photos.
     'jyse-' = GRAPHIC_CLASS_PREFIX in style-applicator/constants.ts. */
  & [data-layer='middle'] > [class^='jyse-'] {
    z-index: ${({ theme }) => theme.zIndices.docked + 1};
  }
  /* Full-viewport-width header photos (fullWidth, photoSlider, photoTrio, photoDuoLeft,
     photoDuoRight, photoMarquee, videoArch) render above art; page-width / constrained
     photos have no explicit z-index and fall below the art sprites. */
  & [data-photo-width='full'] {
    position: relative;
    z-index: ${({ theme }) => theme.zIndices.docked + 2};
  }
  ${({ loading }) => (loading ? hideOnLoading : showOnLoading)};
  ${props =>
    props.customBackgroundColor &&
    css`
      background-color: ${props.customBackgroundColor} !important;
    `}
  ${props =>
    /* Use loadingBackgroundColor as fallback during loading when customBackgroundColor isn't set */
    props.loading &&
    !props.customBackgroundColor &&
    props.loadingBackgroundColor &&
    css`
      background-color: ${props.loadingBackgroundColor};
    `}
  ${props =>
    props.customTextColor &&
    css`
      color: ${props.customTextColor} !important;
    `}
`;
